import { createContextApi, invalidatesOne, providesList, providesOne, createCursorPagedEndpoint, CACHE_POLICY } from '@quickbee/store';
import { getContextClient } from '@quickbee/api-client';

// src/index.ts
var TENANT_TAGS = [
  "Tenant",
  "SubscriptionPlan",
  "PlatformApplication",
  "Branch",
  "StaffUser",
  "ChannelCharge",
  "FeatureFlag",
  "Settings",
  "PublicBranding",
  "TenantDocument",
  "TenantBankAccount",
  "UnitPolicyOverride"
];
var PLATFORM_SCOPE = "PLATFORM";
var publicTenantEndpoints = (build) => ({
  getPublicBrandingByHost: build.query({
    query: (host) => ({ url: `/tenants/public/by-host/${encodeURIComponent(host)}`, publicRoute: true }),
    providesTags: (_r, _e, host) => [{ type: "PublicBranding", id: host }],
    // Branding is stable and cacheable; the backend serves it with a long
    // Cache-Control and a tenant-scoped ETag.
    keepUnusedDataFor: 900
  })
});
var tenantEndpoints = (build) => ({
  // ─── Tenants ────────────────────────────────────────────────────────────────
  /**
   * Rows are `TenantSummaryResponse`, not `TenantResponse`.
   *
   * **This endpoint pages for real.** tenant-service used to build every page with
   * `Page.ofOffset(…, offset = 0, …)` and therefore never returned a `nextCursor` —
   * `hasMore` was `true` with nothing to follow, and no caller could see past the
   * first window. It now uses the platform's offset-cursor scheme, so
   * `nextPageArgs` yields real page-2 args and a "load more" control advances.
   *
   * `size` is bounded 1–100 by `PageRequest`; `total` is a real `COUNT` over the
   * same predicate as the rows, status filter included, so a `size=1` probe is an
   * exact platform-wide tally for that status.
   *
   * `search` is a case-insensitive substring over the four identity columns the
   * console shows: tenant name, tenant code, primary admin name and city.
   */
  listTenants: build.query({
    query: (params) => ({ url: "/tenants", params }),
    ...createCursorPagedEndpoint(),
    providesTags: (result) => providesList("Tenant", result)
  }),
  /**
   * The same read, paged **discretely** — one request, one page, no accumulation.
   *
   * {@link listTenants} drops `cursor` from its cache key and appends every response into one
   * entry, which is right for a "load more" list and wrong for a numbered pager: paging forward
   * would grow the rendered set instead of replacing it, and paging *back* would re-fetch a
   * cursor already walked and append its rows a second time.
   *
   * This one keeps the cursor in the cache key, so each page is its own entry — which also makes
   * *Previous* free, answered from cache rather than re-fetched.
   *
   * Both exist because both are legitimate, and for the reason `api-audit` states about its own
   * pair: a reader scanning a list wants "more", a reader working through one wants "page 3 of 9".
   * Picking one shape for the package would have made the other lie about what is on screen.
   *
   * Pair it with `useCursorPager` — `size`, `status` and `search` are the reset key, the cursor is
   * the walk. A page number cannot be *addressed* here (`PageRequestArgumentResolver` binds only
   * `size`, `cursor` and `sort`), but `pagination.total` is a real `COUNT` over the same predicate
   * as the rows, so every page number the pager draws is a page that exists.
   */
  listTenantsPage: build.query({
    query: (params) => ({ url: "/tenants", params }),
    providesTags: (result) => providesList("Tenant", result)
  }),
  getTenant: build.query({
    query: (id) => ({ url: `/tenants/${id}` }),
    providesTags: (_r, _e, id) => providesOne("Tenant", id),
    keepUnusedDataFor: CACHE_POLICY.reference.keepUnusedDataFor
  }),
  createTenant: build.mutation({
    query: ({ draftId, body }) => ({
      url: "/tenants",
      method: "POST",
      body,
      idempotencyKey: `tenant-create:${draftId}`
    }),
    invalidatesTags: [{ type: "Tenant", id: "LIST" }]
  }),
  /** Full-replace profile update — send the whole profile, not a patch. */
  updateTenant: build.mutation({
    query: ({ id, requestId, body }) => ({
      url: `/tenants/${id}`,
      method: "PUT",
      body,
      idempotencyKey: `tenant-update:${id}:${requestId}`
    }),
    invalidatesTags: (_r, _e, { id }) => invalidatesOne("Tenant", id)
  }),
  /**
   * The tenant's own half of the profile — contact, address, website, GSTIN, PAN.
   *
   * A separate mutation rather than a flag on {@link updateTenant} because it is a separate
   * *permission* (`tenants:update-profile`, not a narrower scope of `tenants:update`) over a body
   * that structurally cannot carry a name or a plan. A `TENANT_ADMIN` may call this and not that.
   *
   * Its own idempotency prefix, so a retry of a profile edit can never be mistaken for a replay of
   * a full tenant update against the same id and request id.
   */
  updateTenantProfile: build.mutation({
    query: ({ id, requestId, body }) => ({
      url: `/tenants/${id}/profile`,
      method: "PUT",
      body,
      idempotencyKey: `tenant-profile:${id}:${requestId}`
    }),
    invalidatesTags: (_r, _e, { id }) => invalidatesOne("Tenant", id)
  }),
  /** Returns the whole tenant, not the branding fragment that was submitted. */
  updateBranding: build.mutation({
    query: ({ id, requestId, body }) => ({
      url: `/tenants/${id}/branding`,
      method: "PUT",
      body,
      idempotencyKey: `tenant-branding:${id}:${requestId}`
    }),
    invalidatesTags: (_r, _e, { id }) => invalidatesOne("Tenant", id)
  }),
  /**
   * The tenant's books (currency-ledger-design ADR-C2, R35). The whole intended
   * state, not a delta — the complete ranking and the complete closure set.
   *
   * **Enable-only**, and the reason is not a policy choice: a book holds
   * append-only ledger entries, so it can be closed to new schemes but never
   * withdrawn. Every unit already enabled must appear in `units` or the call is
   * refused.
   *
   * A currency book additionally needs a **verified dedicated collection
   * account** behind it (D19) — or an explicit `memoMode` ask (D16), which is
   * never a fallback. The refusal names which of the two is missing.
   *
   * Keyed on a caller-supplied `requestId`, not the tenant id: a re-rank is
   * repeatable and a key naming only the tenant would replay the first one for
   * 24 hours.
   */
  updateLedgerUnits: build.mutation({
    query: ({ id, requestId, units, closed, memoMode }) => ({
      url: `/tenants/${id}/ledger-units`,
      method: "PUT",
      body: { units, closed: closed ?? [], memoMode: memoMode ?? [] },
      idempotencyKey: `tenant-ledger-units:${id}:${requestId}`
    }),
    invalidatesTags: (_r, _e, { id }) => invalidatesOne("Tenant", id)
  }),
  updateSpread: build.mutation({
    query: ({ id, requestId, spreadPercent }) => ({
      url: `/tenants/${id}/spread`,
      method: "PUT",
      body: { spreadPercent },
      idempotencyKey: `tenant-spread:${id}:${requestId}`
    }),
    invalidatesTags: (_r, _e, { id }) => invalidatesOne("Tenant", id)
  }),
  // ─── Subscription plans (the commercial catalogue) ──────────────────────────
  /**
   * The plan catalogue. **Unpaged** — a plain array inside an `ApiResponse`, like branches and
   * channel charges, because the catalogue is a handful of commercial tiers and every caller wants
   * all of it.
   *
   * Reads take `tenants:read`, not `tenants:manage-plans`, so the onboarding wizard and
   * `PLATFORM_SUPPORT` can see what is on sale without being able to edit the price list.
   *
   * These routes sit under `/tenants` rather than a prefix of their own so they ride the tenant
   * gateway's existing greedy route — no new stack, no registry entry. `/tenants/plans` can never
   * be read as a tenant id: Spring scores a literal segment above a capture.
   */
  listSubscriptionPlans: build.query({
    query: (args) => ({
      url: "/tenants/plans",
      params: args ?? {}
    }),
    providesTags: (result) => providesList("SubscriptionPlan", result),
    keepUnusedDataFor: CACHE_POLICY.reference.keepUnusedDataFor
  }),
  /**
   * The applications a plan may include. Configuration on the backend, so a newly deployed console
   * appears here without a frontend change — never hard-code this list.
   */
  listPlatformApplications: build.query({
    query: () => ({ url: "/tenants/applications" }),
    providesTags: [{ type: "PlatformApplication", id: "LIST" }],
    keepUnusedDataFor: CACHE_POLICY.reference.keepUnusedDataFor
  }),
  getSubscriptionPlan: build.query({
    query: (planId) => ({ url: `/tenants/plans/${planId}` }),
    providesTags: (_r, _e, planId) => providesOne("SubscriptionPlan", planId),
    keepUnusedDataFor: CACHE_POLICY.reference.keepUnusedDataFor
  }),
  /** Drafts a tier. `409 DUPLICATE_PLAN_CODE` when the code is taken (not a 422). */
  createSubscriptionPlan: build.mutation({
    query: ({ draftId, body }) => ({
      url: "/tenants/plans",
      method: "POST",
      body,
      idempotencyKey: `subscription-plan-create:${draftId}`
    }),
    invalidatesTags: [{ type: "SubscriptionPlan", id: "LIST" }]
  }),
  /** Full replace. `planCode` is not editable; an ARCHIVED plan is refused with a 409. */
  updateSubscriptionPlan: build.mutation({
    query: ({ planId, requestId, body }) => ({
      url: `/tenants/plans/${planId}`,
      method: "PUT",
      body,
      idempotencyKey: `subscription-plan-update:${planId}:${requestId}`
    }),
    invalidatesTags: (_r, _e, { planId }) => [
      ...invalidatesOne("SubscriptionPlan", planId),
      // A price or app-list edit changes what the tenant list and every tenant detail page render.
      { type: "Tenant", id: "LIST" }
    ]
  }),
  /**
   * `DRAFT → PUBLISHED`. Keyed on the plan id alone — see `SubscriptionPlanLifecycleArgs`.
   * Invalidates the list because publishing is what makes a tier appear in the wizard's picker.
   */
  publishSubscriptionPlan: build.mutation({
    query: ({ planId }) => ({
      url: `/tenants/plans/${planId}/publish`,
      method: "POST",
      idempotencyKey: `subscription-plan-publish:${planId}`
    }),
    invalidatesTags: (_r, _e, { planId }) => invalidatesOne("SubscriptionPlan", planId)
  }),
  /**
   * Withdraws a tier from sale. Tenants already on it **keep it** — this is not a downgrade, and
   * no tenant cache needs invalidating as a result.
   */
  archiveSubscriptionPlan: build.mutation({
    query: ({ planId }) => ({
      url: `/tenants/plans/${planId}/archive`,
      method: "POST",
      idempotencyKey: `subscription-plan-archive:${planId}`
    }),
    invalidatesTags: (_r, _e, { planId }) => invalidatesOne("SubscriptionPlan", planId)
  }),
  /**
   * Permanently removes a never-published DRAFT. `204`, no body — see
   * {@link DiscardSubscriptionPlanArgs} for why this is not archive.
   *
   * Keyed on the plan id alone, like publish and archive: the aggregate refuses the call from any
   * state but "drafted and never published", so repeating it genuinely is the same intent. Returns
   * `409 INVALID_PLAN_TRANSITION` for anything else.
   */
  discardSubscriptionPlan: build.mutation({
    query: ({ planId }) => ({
      url: `/tenants/plans/${planId}`,
      method: "DELETE",
      idempotencyKey: `subscription-plan-discard:${planId}`
    }),
    invalidatesTags: (_r, _e, { planId }) => invalidatesOne("SubscriptionPlan", planId)
  }),
  // ─── Collection-channel charges ─────────────────────────────────────────────
  listChannelCharges: build.query({
    query: (id) => ({ url: `/tenants/${id}/channel-charges` }),
    providesTags: (_r, _e, id) => [{ type: "ChannelCharge", id }],
    keepUnusedDataFor: CACHE_POLICY.reference.keepUnusedDataFor
  }),
  /**
   * Upserts one channel's policy and returns the tenant's **whole** charge list.
   * Guarded by `tenants:manage-charges`, deliberately narrower than
   * `tenants:update`: this decides how much of a customer's money becomes gold.
   */
  updateChannelCharge: build.mutation({
    query: ({ id, channel, requestId, body }) => ({
      url: `/tenants/${id}/channel-charges/${channel}`,
      method: "PUT",
      body,
      idempotencyKey: `tenant-channel-charge:${id}:${channel}:${requestId}`
    }),
    invalidatesTags: (_r, _e, { id }) => [{ type: "ChannelCharge", id }]
  }),
  // ─── Basic settings (regional + appearance defaults) ────────────────────────
  //
  // Two scopes, one response shape. `PLATFORM_SCOPE` is reused as the cache id
  // for the platform tier — it has no tenant id, exactly like the platform
  // feature-flag catalogue above.
  //
  // A tenant write invalidates the platform entry too. It cannot change the
  // platform tier, but the tenant response embeds `inherited`, and a screen
  // that had both scopes in cache would otherwise keep rendering a stale
  // "Inherited from platform" line beside a value that just moved.
  /** Platform-wide defaults, plus the shipped fallbacks they resolve against. */
  getPlatformSettings: build.query({
    query: () => ({ url: "/tenants/settings" }),
    providesTags: [{ type: "Settings", id: PLATFORM_SCOPE }],
    keepUnusedDataFor: CACHE_POLICY.reference.keepUnusedDataFor
  }),
  /** One tenant's overrides, the platform tier it inherits, and the resolved result. */
  getTenantSettings: build.query({
    query: (id) => ({ url: `/tenants/${id}/settings` }),
    providesTags: (_r, _e, id) => [{ type: "Settings", id }],
    keepUnusedDataFor: CACHE_POLICY.reference.keepUnusedDataFor
  }),
  /**
   * Full replace of the platform defaults — a null field clears it back to the
   * shipped value. Returns the refreshed projection, not a `204`.
   *
   * Invalidates **every** tenant's settings entry (`id: 'LIST'` is not enough —
   * these are per-id tags), because each tenant response carries the platform
   * tier inside `inherited`.
   */
  updatePlatformSettings: build.mutation({
    query: ({ requestId, body }) => ({
      url: "/tenants/settings",
      method: "PUT",
      body,
      // Keyed on the intent, not on the scope: an entity-only key would make a
      // change → revert → same-change cycle inside the filter's 24-hour window
      // replay the first write and never execute the third.
      idempotencyKey: `tenant-settings:${PLATFORM_SCOPE}:${requestId}`
    }),
    // The bare tag name, not `{ type }` — `TagDescriptor` spells "every tag of this type" as
    // the string alone, and the object form requires an id it would have to invent.
    invalidatesTags: () => ["Settings"]
  }),
  /**
   * Full replace of one tenant's overrides — a null field clears it, returning
   * that field to tracking the platform default.
   */
  updateTenantSettings: build.mutation({
    query: ({ id, requestId, body }) => ({
      url: `/tenants/${id}/settings`,
      method: "PUT",
      body,
      idempotencyKey: `tenant-settings:${id}:${requestId}`
    }),
    invalidatesTags: (_r, _e, { id }) => [
      { type: "Settings", id },
      // The theme a logged-out client renders with now resolves from these
      // values, so the pre-login branding read is stale after this write. Bare
      // tag name: it is keyed by *host*, and one tenant may answer on several.
      "PublicBranding"
    ]
  }),
  // ─── Feature flags ──────────────────────────────────────────────────────────
  /** Platform-scope catalogue: every entry's `tenantOverride` is `null`. */
  getPlatformFeatureFlagCatalog: build.query({
    query: () => ({ url: "/tenants/feature-flags" }),
    providesTags: [{ type: "FeatureFlag", id: PLATFORM_SCOPE }],
    keepUnusedDataFor: CACHE_POLICY.reference.keepUnusedDataFor
  }),
  /** Tenant-scope catalogue: platform defaults plus this tenant's overrides. */
  getTenantFeatureFlagCatalog: build.query({
    query: (id) => ({ url: `/tenants/${id}/feature-flags` }),
    providesTags: (_r, _e, id) => [{ type: "FeatureFlag", id }],
    keepUnusedDataFor: CACHE_POLICY.reference.keepUnusedDataFor
  }),
  /**
   * `204`. Send only the keys being changed — the map must be non-empty.
   *
   * **Three-valued**: `true`/`false` pin the tenant's position, `null` clears the override so the
   * flag inherits the platform default again. See {@link FeatureFlagWrite} for why those are not
   * two ways of saying the same thing. Sets and clears may be mixed in one call and land as a
   * single edit; a call that leaves every flag where it already was writes nothing at all.
   */
  toggleFeatureFlags: build.mutation({
    query: ({ id, requestId, changes }) => ({
      url: `/tenants/${id}/feature-flags`,
      method: "PUT",
      body: { changes },
      idempotencyKey: `tenant-feature-flags:${id}:${requestId}`
    }),
    invalidatesTags: (_r, _e, { id }) => [{ type: "FeatureFlag", id }]
  }),
  // ─── Tenant lifecycle ───────────────────────────────────────────────────────
  /**
   * `ONBOARDING → ACTIVE`, and rejected from any other status, so the entity id
   * is a sufficient idempotency key. Also rejected while `schemaProvisioned` is
   * `false` — check that before enabling the control.
   */
  activateTenant: build.mutation({
    query: (id) => ({
      url: `/tenants/${id}/activate`,
      method: "POST",
      idempotencyKey: `tenant-activate:${id}`
    }),
    invalidatesTags: (_r, _e, id) => invalidatesOne("Tenant", id)
  }),
  /**
   * The operator finished onboarding and is handing the tenant to Approvals.
   *
   * **Not a lifecycle transition** — it neither changes `status` nor grants anything, and the
   * tenant stays `ONBOARDING`. It records the fact the Approvals queue was missing: which of the
   * tenants sitting there is finished, rather than one somebody abandoned mid-wizard.
   *
   * Keyed on the entity alone, like `activateTenant` and for a stronger reason: the backend keeps
   * the first instant on a re-submission, so a replay and a genuine retry are the same write.
   */
  submitTenantForApproval: build.mutation({
    query: (id) => ({
      url: `/tenants/${id}/submit`,
      method: "POST",
      idempotencyKey: `tenant-submit:${id}`
    }),
    invalidatesTags: (_r, _e, id) => invalidatesOne("Tenant", id)
  }),
  /** `ACTIVE → SUSPENDED`. Reversible, so the key carries `requestId`. */
  suspendTenant: build.mutation({
    query: ({ id, requestId, reason }) => ({
      url: `/tenants/${id}/suspend`,
      method: "POST",
      body: { reason },
      idempotencyKey: `tenant-suspend:${id}:${requestId}`
    }),
    invalidatesTags: (_r, _e, { id }) => invalidatesOne("Tenant", id)
  }),
  /** `SUSPENDED → ACTIVE`. Reversible, so the key carries `requestId`. */
  reactivateTenant: build.mutation({
    query: ({ id, requestId }) => ({
      url: `/tenants/${id}/reactivate`,
      method: "POST",
      idempotencyKey: `tenant-reactivate:${id}:${requestId}`
    }),
    invalidatesTags: (_r, _e, { id }) => invalidatesOne("Tenant", id)
  }),
  /**
   * `204`, terminal and irreversible (INV-16), and it takes **no body** — the
   * reason field belongs to `suspend` alone. Keyed on the id because a second
   * archive is rejected by the aggregate anyway.
   */
  /**
   * Terminal and irreversible (INV-16).
   *
   * Refused with a 409 while the tenant still holds customers or active
   * schemes, naming both counts — the same confirmable-refusal shape branch
   * deactivation uses for INV-11. Re-call with `force: true` once the operator
   * has read the refusal.
   *
   * The idempotency key names only the tenant, and correctly: archiving is
   * rejected from any state but the ones that precede it, so a repeat genuinely
   * is the same intent. `force` is deliberately outside the key — a confirmed
   * retry is the *same* archive, not a different one.
   */
  archiveTenant: build.mutation({
    query: ({ id, force }) => ({
      url: `/tenants/${id}/archive${force ? "?force=true" : ""}`,
      method: "POST",
      idempotencyKey: `tenant-archive:${id}`
    }),
    invalidatesTags: (_r, _e, { id }) => invalidatesOne("Tenant", id)
  }),
  /** Repeatable by definition, so the key must carry `requestId`. */
  retryProvisioning: build.mutation({
    query: ({ id, requestId }) => ({
      url: `/tenants/${id}/retry-provisioning`,
      method: "POST",
      idempotencyKey: `tenant-retry-provisioning:${id}:${requestId}`
    }),
    invalidatesTags: (_r, _e, { id }) => invalidatesOne("Tenant", id)
  }),
  // ─── Branches ───────────────────────────────────────────────────────────────
  /** A plain array — `TenantBranchesController#list` takes no `PageRequest`. */
  listBranches: build.query({
    query: (tenantId) => ({ url: `/tenants/${tenantId}/branches` }),
    providesTags: (result) => providesList("Branch", result)
  }),
  getBranch: build.query({
    query: ({ tenantId, branchId }) => ({ url: `/tenants/${tenantId}/branches/${branchId}` }),
    providesTags: (_r, _e, { branchId }) => providesOne("Branch", branchId)
  }),
  createBranch: build.mutation({
    query: ({ tenantId, draftId, body }) => ({
      url: `/tenants/${tenantId}/branches`,
      method: "POST",
      body,
      idempotencyKey: `branch-create:${tenantId}:${draftId}`
    }),
    invalidatesTags: [{ type: "Branch", id: "LIST" }]
  }),
  /** Full replace; `branchCode` is immutable and is not part of the body. */
  updateBranch: build.mutation({
    query: ({ tenantId, branchId, requestId, body }) => ({
      url: `/tenants/${tenantId}/branches/${branchId}`,
      method: "PUT",
      body,
      idempotencyKey: `branch-update:${branchId}:${requestId}`
    }),
    invalidatesTags: (_r, _e, { branchId }) => invalidatesOne("Branch", branchId)
  }),
  /**
   * INV-11: 409s with `activeStaffCount` while staff are still assigned. Confirm
   * with the operator, then re-call with `force: true` and the **same**
   * `requestId` — the first call never reached the handler, so its key was never
   * stored, and reusing it keeps the confirmed retry a single logical action.
   */
  deactivateBranch: build.mutation({
    query: ({ tenantId, branchId, requestId, force }) => ({
      url: `/tenants/${tenantId}/branches/${branchId}/deactivate`,
      method: "POST",
      params: { force },
      idempotencyKey: `branch-deactivate:${branchId}:${requestId}`
    }),
    invalidatesTags: (_r, _e, { branchId }) => invalidatesOne("Branch", branchId)
  }),
  reactivateBranch: build.mutation({
    query: ({ tenantId, branchId, requestId }) => ({
      url: `/tenants/${tenantId}/branches/${branchId}/reactivate`,
      method: "POST",
      idempotencyKey: `branch-reactivate:${branchId}:${requestId}`
    }),
    invalidatesTags: (_r, _e, { branchId }) => invalidatesOne("Branch", branchId)
  }),
  // ─── Tenant staff users ─────────────────────────────────────────────────────
  /**
   * The tenant's staff roster. **Pages for real**, on the same offset-cursor scheme as
   * `listTenants` — it did not until 2026-08-27, when every page came back with a `null`
   * `nextCursor` and the console's Users tab could see one 100-row window and no more.
   */
  listStaffUsers: build.query({
    query: ({ tenantId, ...params }) => ({
      url: `/tenants/${tenantId}/users`,
      params
    }),
    ...createCursorPagedEndpoint(),
    providesTags: (result) => providesList("StaffUser", result)
  }),
  /**
   * The roster, paged **discretely** — the {@link listTenantsPage} twin of {@link listStaffUsers},
   * for the same stated reason.
   *
   * Both readers are real here rather than hypothetical, which is why neither endpoint replaced
   * the other: the console's Users tab pages through the roster, and the Approvals panel's
   * activation check *scans* it — it has to know whether it has read the whole thing before it can
   * report that a tenant has no administrator, and that is accumulating semantics.
   *
   * There is no `search` on this route. A grid driven by this endpoint must therefore set
   * `searchable={false}`: `DataGrid`'s server-side mode does not filter, so a box left switched on
   * would render, accept text and narrow nothing.
   */
  listStaffUsersPage: build.query({
    query: ({ tenantId, ...params }) => ({
      url: `/tenants/${tenantId}/users`,
      params
    }),
    providesTags: (result) => providesList("StaffUser", result)
  }),
  getStaffUser: build.query({
    query: ({ tenantId, userId }) => ({ url: `/tenants/${tenantId}/users/${userId}` }),
    providesTags: (_r, _e, { userId }) => providesOne("StaffUser", userId)
  }),
  /** `role` accepts the display string (`'Tenant Admin'`) or the enum constant. */
  inviteStaffUser: build.mutation({
    query: ({ tenantId, draftId, body }) => ({
      url: `/tenants/${tenantId}/users`,
      method: "POST",
      body,
      idempotencyKey: `staff-invite:${tenantId}:${draftId}`
    }),
    invalidatesTags: [{ type: "StaffUser", id: "LIST" }]
  }),
  /**
   * The whole batch in one request — the server runs each row as its own invite, in its own
   * transactions, and itemises the rows it refused. One key for the batch: the response is the
   * per-row report, so a retry wants the first run's summary back, not a second run.
   */
  bulkInviteStaffUsers: build.mutation({
    query: ({ tenantId, draftId, body }) => ({
      url: `/tenants/${tenantId}/users/bulk`,
      method: "POST",
      body,
      idempotencyKey: `staff-bulk-invite:${tenantId}:${draftId}`
    }),
    invalidatesTags: [{ type: "StaffUser", id: "LIST" }]
  }),
  /** Full replace; `email` is immutable and is not part of the body. */
  updateStaffUser: build.mutation({
    query: ({ tenantId, userId, requestId, body }) => ({
      url: `/tenants/${tenantId}/users/${userId}`,
      method: "PUT",
      body,
      idempotencyKey: `staff-update:${userId}:${requestId}`
    }),
    invalidatesTags: (_r, _e, { userId }) => invalidatesOne("StaffUser", userId)
  }),
  /**
   * Three-state, and cyclic — `requestId` is what keeps an
   * ACTIVE → SUSPENDED → ACTIVE sequence from replaying a cached response.
   */
  changeStaffUserStatus: build.mutation({
    query: ({ tenantId, userId, requestId, status }) => ({
      url: `/tenants/${tenantId}/users/${userId}/status`,
      method: "POST",
      body: { status },
      idempotencyKey: `staff-status:${userId}:${requestId}`
    }),
    invalidatesTags: (_r, _e, { userId }) => invalidatesOne("StaffUser", userId)
  }),
  /**
   * Creates or links roster rows for logins that hold staff grants here but were minted outside
   * the invite flow — a provisioning script, a row from before the roster and the grant were
   * unified. Additive and convergent; `requestId` is per click so a deliberate re-run inside the
   * idempotency window reconciles again instead of replaying the first summary.
   */
  /**
   * Starts a password reset for one of this tenant's staff members.
   *
   * **Not `api-identity`'s `resetUserPassword`, deliberately.** identity keeps user accounts in
   * each tenant's own schema and resolves one within the tenant on the request's token, and
   * `JwtAuthenticationFilter` refuses an `X-Tenant-ID` that disagrees with that token — so a
   * platform administrator resetting a jeweller's Tenant Admin searched QuickBee Everywhere's
   * schema and got a 404 rendered as "we couldn't find what you were looking for". Routed through
   * tenant-service, the reset runs under the tenant whose roster names the person.
   *
   * `api-identity`'s version is still right for a caller acting on their **own** tenant, and is
   * what the identity console uses.
   *
   * Keyed per attempt, never per user: a second reset exists because the first code never arrived,
   * and a stable key would replay the first answer and hand the administrator a success toast with
   * no second code behind it.
   *
   * Invalidates nothing — no roster field changes, and the reset state lives at the identity
   * provider.
   */
  resetStaffPassword: build.mutation({
    query: ({ tenantId, userId, requestId }) => ({
      url: `/tenants/${tenantId}/users/${userId}/password-reset`,
      method: "POST",
      idempotencyKey: `staff-password-reset:${userId}:${requestId}`
    })
  }),
  /**
   * A second person's decision on a privileged staff invitation.
   *
   * Approving is what **creates** the login: a pending row has no identity account at all, so this
   * is not a flag flipped beside an account that already works — which is the difference between a
   * control and a formality. Refusing is terminal and leaves the row as the record of the decision.
   *
   * 409 `SELF_APPROVAL_FORBIDDEN` when the caller is the person who asked, the same refusal INV-17
   * gives a KYC maker who tries to check their own case. It is not a permissions problem, and a
   * screen that renders it as one sends a reviewer off to ask for authority they already hold.
   *
   * Keyed per attempt rather than per decision so a genuine retry after a network error reaches
   * the server, and invalidates the row it decided — the status, and (on approval) the identity
   * link the provisioning just created.
   */
  decideStaffApproval: build.mutation({
    query: ({ tenantId, userId, requestId, approved }) => ({
      url: `/tenants/${tenantId}/users/${userId}/approval`,
      method: "POST",
      body: { approved },
      idempotencyKey: `staff-approval:${userId}:${requestId}`
    }),
    invalidatesTags: (_r, _e, { userId }) => invalidatesOne("StaffUser", userId)
  }),
  /**
   * Sends a staff member's invitation again, with a fresh code that sets their first password.
   *
   * The repair for the two ordinary ways an invitation goes missing — it never arrived, or it
   * expired before the new starter got to it — and, until the invite began sending one at all,
   * the answer to a question nobody could ask. Same rail as the reset: the code is single-use, it
   * goes to the person's own contacts, and it is completed at the same confirm screen.
   *
   * Keyed per attempt for the same reason the reset is: a second invitation exists because the
   * first one did not land, and a stable key would replay the first answer and show a success
   * toast with no second message behind it.
   */
  resendStaffInvitation: build.mutation({
    query: ({ tenantId, userId, requestId }) => ({
      url: `/tenants/${tenantId}/users/${userId}/invitation`,
      method: "POST",
      idempotencyKey: `staff-invitation:${userId}:${requestId}`
    })
  }),
  /**
   * The staff roles the signed-in caller may assign on this tenant.
   *
   * Read rather than derived. The matrix depends on who is asking and whose tenant it is —
   * QuickBee staffing its own organisation, QuickBee appointing a jeweller's administrator, and
   * that administrator hiring their own people are three different lists — and the server refuses
   * anything outside the answer with a 403. A copy of the rule in TypeScript would be a second
   * thing to keep in step with `StaffRoleAssignmentPolicy`, and the first one to fall behind.
   *
   * Cached against the tenant, not a list: it is a fact about the caller and the tenant, and it
   * changes only when one of them does.
   */
  assignableStaffRoles: build.query({
    query: ({ tenantId }) => ({ url: `/tenants/${tenantId}/users/assignable-roles` }),
    providesTags: (_r, _e, { tenantId }) => [
      { type: "StaffUser", id: `ASSIGNABLE-${tenantId}` }
    ]
  }),
  reconcileStaffRoster: build.mutation({
    query: ({ tenantId, requestId }) => ({
      url: `/tenants/${tenantId}/users/reconcile`,
      method: "POST",
      idempotencyKey: `staff-reconcile:${tenantId}:${requestId}`
    }),
    invalidatesTags: [{ type: "StaffUser", id: "LIST" }]
  }),
  // ─── Tenant documents ──────────────────────────────────────────────────────
  //
  // Upload is three legs and only two of them are here: leg 2 is a raw PUT
  // straight to object storage, issued by `uploadToPresignedUrl` in
  // `@quickbee/api-client`. It must not go through this client — an
  // `Authorization` or `X-Tenant-ID` header on a presigned URL invalidates the
  // signature.
  //
  //   const ticket = await requestDocumentUpload({ id, draftId, body }).unwrap();
  //   await uploadToPresignedUrl({
  //     ticket: { uploadUrl: ticket.uploadUrl, headers: ticket.requiredHeaders },
  //     file,
  //     confirm: () => confirmDocumentUpload({ id, requestId, documentId: ticket.documentId }).unwrap()
  //   });
  //
  // `requiredHeaders` is already the map shape the helper wants, so unlike
  // api-loan's tickets this needs no per-context adapter.
  requestDocumentUpload: build.mutation({
    query: ({ id, draftId, body }) => ({
      url: `/tenants/${id}/documents/upload-url`,
      method: "POST",
      body,
      // Scoped to the action, not the tenant: an entity-only key would make the
      // second upload for a tenant replay the first ticket, handing the caller a
      // URL for somebody else's file.
      idempotencyKey: `tenant-doc-upload:${id}:${draftId}`
    })
    // Deliberately invalidates nothing: leg 1 creates a PENDING row no list shows.
    // The list refreshes on confirm, when there is something to show.
  }),
  confirmDocumentUpload: build.mutation({
    query: ({ id, documentId, requestId }) => ({
      url: `/tenants/${id}/documents/${documentId}/confirm`,
      method: "POST",
      idempotencyKey: `tenant-doc-confirm:${documentId}:${requestId}`
    }),
    invalidatesTags: (_r, _e, { id }) => [
      { type: "TenantDocument", id: "LIST" },
      // The tenant's own branding may now be pointing at this document.
      ...invalidatesOne("Tenant", id)
    ]
  }),
  listTenantDocuments: build.query({
    query: ({ id, documentType }) => ({
      url: `/tenants/${id}/documents`,
      // Spread rather than a ternary yielding `undefined`: under
      // exactOptionalPropertyTypes an explicit `params: undefined` is not the
      // same as an absent one, and only the absent one type-checks.
      ...documentType ? { params: { documentType } } : {}
    }),
    providesTags: (result) => providesList("TenantDocument", result)
  }),
  /**
   * Private documents only — a brand asset already has a permanent address and
   * the backend refuses to mint an expiring one alongside it.
   *
   * Not cached: the URL it returns stops working in minutes, so a cached
   * response is worse than no response. Read it, use it, discard it.
   */
  getTenantDocumentDownloadUrl: build.query({
    query: ({ id, documentId }) => ({
      url: `/tenants/${id}/documents/${documentId}/download-url`
    }),
    keepUnusedDataFor: 0
  }),
  /** Refused while the document is the tenant's current logo. */
  deleteTenantDocument: build.mutation({
    query: ({ id, documentId, requestId }) => ({
      url: `/tenants/${id}/documents/${documentId}`,
      method: "DELETE",
      idempotencyKey: `tenant-doc-delete:${documentId}:${requestId}`
    }),
    invalidatesTags: () => [{ type: "TenantDocument", id: "LIST" }]
  }),
  // ─── The dedicated collection account (currency-ledger-design D1, D19) ──────
  //
  // Every write returns the tenant's whole account list, not the one row it
  // touched: the six actions are one workflow over at most a couple of accounts,
  // and a screen that has to merge a single-row response into a list it also
  // holds is a screen with two sources of truth for the same fact.
  //
  // `by-unit` is deliberately absent. It is VPC-internal, read by ledger-service
  // before each bank reconciliation, and is not routed through the gateway.
  listTenantBankAccounts: build.query({
    query: (id) => ({ url: `/tenants/${id}/bank-accounts` }),
    providesTags: (result) => providesList("TenantBankAccount", result, "id")
  }),
  registerTenantBankAccount: build.mutation({
    query: ({ id, requestId, ...body }) => ({
      url: `/tenants/${id}/bank-accounts`,
      method: "POST",
      body,
      idempotencyKey: `tenant-bank-account:${id}:${requestId}`
    }),
    invalidatesTags: () => [{ type: "TenantBankAccount", id: "LIST" }]
  }),
  /** Refused once VERIFIED — suspend and register the replacement instead. */
  amendTenantBankAccount: build.mutation({
    query: ({ id, accountId, requestId, ...body }) => ({
      url: `/tenants/${id}/bank-accounts/${accountId}`,
      method: "PUT",
      body,
      idempotencyKey: `tenant-bank-account-amend:${accountId}:${requestId}`
    }),
    invalidatesTags: () => [{ type: "TenantBankAccount", id: "LIST" }]
  }),
  /** The proof document and the gateway's confirmation — at least one required. */
  recordBankAccountEvidence: build.mutation({
    query: ({ id, accountId, requestId, ...body }) => ({
      url: `/tenants/${id}/bank-accounts/${accountId}/evidence`,
      method: "POST",
      body,
      idempotencyKey: `tenant-bank-account-evidence:${accountId}:${requestId}`
    }),
    invalidatesTags: () => [{ type: "TenantBankAccount", id: "LIST" }]
  }),
  /**
   * The tenant admin's single-purpose-use attestation — the one piece of evidence
   * that is a promise rather than a document. `tenant_bank_accounts:attest:tenant`,
   * which the platform operator deliberately does not hold: it cannot make the
   * tenant's promise for it.
   */
  attestTenantBankAccount: build.mutation({
    query: ({ id, accountId, requestId }) => ({
      url: `/tenants/${id}/bank-accounts/${accountId}/attestation`,
      method: "POST",
      idempotencyKey: `tenant-bank-account-attest:${accountId}:${requestId}`
    }),
    invalidatesTags: () => [{ type: "TenantBankAccount", id: "LIST" }]
  }),
  /**
   * The platform operator's verification — the gate in front of opening a rupee
   * book without memo mode, so `tenant_bank_accounts:verify:any` and never the
   * tenant. Refused, naming the missing piece, if any evidence is absent.
   *
   * Invalidates the tenant too: verifying the account is what ends memo mode on
   * its book, and the tenant record is where the console reads that from.
   */
  verifyTenantBankAccount: build.mutation({
    query: ({ id, accountId, requestId }) => ({
      url: `/tenants/${id}/bank-accounts/${accountId}/verify`,
      method: "POST",
      idempotencyKey: `tenant-bank-account-verify:${accountId}:${requestId}`
    }),
    invalidatesTags: (_r, _e, { id }) => [
      { type: "TenantBankAccount", id: "LIST" },
      ...invalidatesOne("Tenant", id)
    ]
  }),
  /** Withdraws the account with a reason. Never closes the book — that is separate. */
  suspendTenantBankAccount: build.mutation({
    query: ({ id, accountId, requestId, reason }) => ({
      url: `/tenants/${id}/bank-accounts/${accountId}/suspend`,
      method: "POST",
      body: { reason },
      idempotencyKey: `tenant-bank-account-suspend:${accountId}:${requestId}`
    }),
    invalidatesTags: (_r, _e, { id }) => [
      { type: "TenantBankAccount", id: "LIST" },
      ...invalidatesOne("Tenant", id)
    ]
  }),
  // ─── D21's unit-policy overrides ────────────────────────────────────────────
  //
  // Every route is `:any`, INCLUDING the read: a tenant admin may not see, let
  // alone raise, their own ceiling or caps. The whole content of the control is
  // that the party it binds is not the party that moves it.
  listUnitPolicyOverrides: build.query({
    query: (id) => ({ url: `/tenants/${id}/unit-policy` }),
    providesTags: (result) => providesList("UnitPolicyOverride", result, "id")
  }),
  /**
   * The platform's approvals queue — every tenant's request awaiting a second
   * administrator, oldest first. Unpaged by design: a list of regulatory
   * exceptions nobody has answered should be short enough to read in one screen,
   * and a pager would hide it when it is not.
   */
  listPendingUnitPolicyOverrides: build.query({
    query: () => ({ url: "/tenants/unit-policy/pending" }),
    providesTags: (result) => providesList("UnitPolicyOverride", result, "id")
  }),
  requestUnitPolicyOverride: build.mutation({
    query: ({ id, requestId, ...body }) => ({
      url: `/tenants/${id}/unit-policy`,
      method: "POST",
      body,
      idempotencyKey: `unit-policy-request:${id}:${requestId}`
    }),
    invalidatesTags: () => [
      { type: "UnitPolicyOverride", id: "LIST" }
    ]
  }),
  /**
   * The second administrator's approval. Refused server-side if the caller is the
   * requester — always, with no `allowSelfApproval` setting behind it.
   *
   * Invalidates the tenant as well: an approved ceiling changes what a scheme
   * form may offer, and the screen that asked before the approval must ask again.
   */
  approveUnitPolicyOverride: build.mutation({
    query: ({ id, overrideId, requestId, note }) => ({
      url: `/tenants/${id}/unit-policy/${overrideId}/approve`,
      method: "POST",
      body: { note },
      idempotencyKey: `unit-policy-approve:${overrideId}:${requestId}`
    }),
    invalidatesTags: (_r, _e, { id }) => [
      { type: "UnitPolicyOverride", id: "LIST" },
      ...invalidatesOne("Tenant", id)
    ]
  }),
  /** The note is mandatory here — a refusal nobody explained will be re-asked. */
  rejectUnitPolicyOverride: build.mutation({
    query: ({ id, overrideId, requestId, note }) => ({
      url: `/tenants/${id}/unit-policy/${overrideId}/reject`,
      method: "POST",
      body: { note },
      idempotencyKey: `unit-policy-reject:${overrideId}:${requestId}`
    }),
    invalidatesTags: () => [
      { type: "UnitPolicyOverride", id: "LIST" }
    ]
  }),
  /** Retires an override without refusing it. The row is never deleted. */
  withdrawUnitPolicyOverride: build.mutation({
    query: ({ id, overrideId, requestId, note }) => ({
      url: `/tenants/${id}/unit-policy/${overrideId}/withdraw`,
      method: "POST",
      body: { note },
      idempotencyKey: `unit-policy-withdraw:${overrideId}:${requestId}`
    }),
    invalidatesTags: (_r, _e, { id }) => [
      { type: "UnitPolicyOverride", id: "LIST" },
      ...invalidatesOne("Tenant", id)
    ]
  })
});
async function fetchPublicBranding(host) {
  try {
    const client = getContextClient({ context: "tenant", publicClient: true });
    return await client.get(
      `/tenants/public/by-host/${encodeURIComponent(host)}`,
      { publicRoute: true }
    );
  } catch {
    return null;
  }
}

// src/types.ts
var TENANT_MAX_PAGE_SIZE = 100;

// src/index.ts
var tenantApi = createContextApi({
  context: "tenant",
  reducerPath: "tenantApi",
  tagTypes: TENANT_TAGS,
  endpoints: tenantEndpoints
});
var publicTenantApi = createContextApi({
  context: "tenant",
  reducerPath: "publicTenantApi",
  tagTypes: TENANT_TAGS,
  publicClient: true,
  endpoints: publicTenantEndpoints
});
var {
  // Tenants
  useListTenantsQuery,
  // The discrete twin — one request, one page. Behind a numbered pager, never beside one.
  useListTenantsPageQuery,
  useGetTenantQuery,
  useCreateTenantMutation,
  useUpdateTenantMutation,
  useUpdateTenantProfileMutation,
  useUpdateBrandingMutation,
  useUpdateLedgerUnitsMutation,
  useUpdateSpreadMutation,
  // Subscription plans — the commercial catalogue
  useListSubscriptionPlansQuery,
  useGetSubscriptionPlanQuery,
  useCreateSubscriptionPlanMutation,
  useUpdateSubscriptionPlanMutation,
  usePublishSubscriptionPlanMutation,
  useArchiveSubscriptionPlanMutation,
  useDiscardSubscriptionPlanMutation,
  useListPlatformApplicationsQuery,
  // Collection-channel charges
  useListChannelChargesQuery,
  useUpdateChannelChargeMutation,
  // Basic settings — regional and appearance defaults, platform and per tenant
  useGetPlatformSettingsQuery,
  useGetTenantSettingsQuery,
  useUpdatePlatformSettingsMutation,
  useUpdateTenantSettingsMutation,
  // Feature flags
  useGetPlatformFeatureFlagCatalogQuery,
  useGetTenantFeatureFlagCatalogQuery,
  useToggleFeatureFlagsMutation,
  // Lifecycle
  useActivateTenantMutation,
  // Not a lifecycle transition — grouped here because the wizard's last step and the Approvals
  // queue sit either side of it. See the endpoint.
  useSubmitTenantForApprovalMutation,
  useSuspendTenantMutation,
  useReactivateTenantMutation,
  useArchiveTenantMutation,
  useRetryProvisioningMutation,
  // Branches
  useListBranchesQuery,
  useGetBranchQuery,
  useCreateBranchMutation,
  useUpdateBranchMutation,
  useDeactivateBranchMutation,
  useReactivateBranchMutation,
  // Staff users
  useListStaffUsersQuery,
  useListStaffUsersPageQuery,
  useGetStaffUserQuery,
  useBulkInviteStaffUsersMutation,
  useInviteStaffUserMutation,
  useUpdateStaffUserMutation,
  useChangeStaffUserStatusMutation,
  useDecideStaffApprovalMutation,
  useResendStaffInvitationMutation,
  useAssignableStaffRolesQuery,
  useResetStaffPasswordMutation,
  useReconcileStaffRosterMutation,
  // Tenant documents — brand assets and KYB records
  useRequestDocumentUploadMutation,
  useConfirmDocumentUploadMutation,
  useListTenantDocumentsQuery,
  useLazyGetTenantDocumentDownloadUrlQuery,
  useDeleteTenantDocumentMutation,
  // The dedicated collection account (D1, D19)
  useListTenantBankAccountsQuery,
  useRegisterTenantBankAccountMutation,
  useAmendTenantBankAccountMutation,
  useRecordBankAccountEvidenceMutation,
  useAttestTenantBankAccountMutation,
  useVerifyTenantBankAccountMutation,
  useSuspendTenantBankAccountMutation,
  // D21's unit-policy overrides
  useListUnitPolicyOverridesQuery,
  useListPendingUnitPolicyOverridesQuery,
  useRequestUnitPolicyOverrideMutation,
  useApproveUnitPolicyOverrideMutation,
  useRejectUnitPolicyOverrideMutation,
  useWithdrawUnitPolicyOverrideMutation
} = tenantApi;
var { useGetPublicBrandingByHostQuery } = publicTenantApi;

export { PLATFORM_SCOPE, TENANT_MAX_PAGE_SIZE, TENANT_TAGS, fetchPublicBranding, publicTenantApi, tenantApi, useActivateTenantMutation, useAmendTenantBankAccountMutation, useApproveUnitPolicyOverrideMutation, useArchiveSubscriptionPlanMutation, useArchiveTenantMutation, useAssignableStaffRolesQuery, useAttestTenantBankAccountMutation, useBulkInviteStaffUsersMutation, useChangeStaffUserStatusMutation, useConfirmDocumentUploadMutation, useCreateBranchMutation, useCreateSubscriptionPlanMutation, useCreateTenantMutation, useDeactivateBranchMutation, useDecideStaffApprovalMutation, useDeleteTenantDocumentMutation, useDiscardSubscriptionPlanMutation, useGetBranchQuery, useGetPlatformFeatureFlagCatalogQuery, useGetPlatformSettingsQuery, useGetPublicBrandingByHostQuery, useGetStaffUserQuery, useGetSubscriptionPlanQuery, useGetTenantFeatureFlagCatalogQuery, useGetTenantQuery, useGetTenantSettingsQuery, useInviteStaffUserMutation, useLazyGetTenantDocumentDownloadUrlQuery, useListBranchesQuery, useListChannelChargesQuery, useListPendingUnitPolicyOverridesQuery, useListPlatformApplicationsQuery, useListStaffUsersPageQuery, useListStaffUsersQuery, useListSubscriptionPlansQuery, useListTenantBankAccountsQuery, useListTenantDocumentsQuery, useListTenantsPageQuery, useListTenantsQuery, useListUnitPolicyOverridesQuery, usePublishSubscriptionPlanMutation, useReactivateBranchMutation, useReactivateTenantMutation, useReconcileStaffRosterMutation, useRecordBankAccountEvidenceMutation, useRegisterTenantBankAccountMutation, useRejectUnitPolicyOverrideMutation, useRequestDocumentUploadMutation, useRequestUnitPolicyOverrideMutation, useResendStaffInvitationMutation, useResetStaffPasswordMutation, useRetryProvisioningMutation, useSubmitTenantForApprovalMutation, useSuspendTenantBankAccountMutation, useSuspendTenantMutation, useToggleFeatureFlagsMutation, useUpdateBranchMutation, useUpdateBrandingMutation, useUpdateChannelChargeMutation, useUpdateLedgerUnitsMutation, useUpdatePlatformSettingsMutation, useUpdateSpreadMutation, useUpdateStaffUserMutation, useUpdateSubscriptionPlanMutation, useUpdateTenantMutation, useUpdateTenantProfileMutation, useUpdateTenantSettingsMutation, useVerifyTenantBankAccountMutation, useWithdrawUnitPolicyOverrideMutation };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map